import {t} from "@lingui/core/macro";

import {isEqual, isNil, omit} from "lodash";
import {call, select, take} from "redux-saga/effects";

import {subscribe} from "@atg-frame-shared/push";
import {RECONNECTED} from "@atg-frame-shared/push-saga/domain/pushActions";
import {getSolaceClient} from "@atg-frame-shared/push-saga/domain/pushSaga";
import {
    getClientName,
    getRouteName,
    getWebsocketIds,
} from "@atg-frame-shared/push-saga/helpers";
import * as UserActions from "@atg-global-shared/user/userActionTypes";
import {fetchAuthorized} from "@atg-shared/auth";
import * as AuthSelectors from "@atg-shared/auth/domain/authSelectors";
import log, {serializeError} from "@atg-shared/log";
import {TEAM_SUBSCRIBE_SERVICE_URL} from "@atg-shared/service-url";
import {TillsammansAnalytics} from "@atg-tillsammans/analytics";
import * as BetActions from "@atg-tillsammans/bet-data-access/common/redux/betActions";
import * as SportBetActions from "@atg-tillsammans/bet-data-access/sport/redux/sportBetActions";
import {setHorseCouponPushData} from "@atg-tillsammans/coupon-data-access";
import {HorseCouponPushEvent} from "@atg-tillsammans/coupon-types";
import {GameUtils} from "@atg-tillsammans/game-utils";
import {HorseReceiptPushEvent} from "@atg-tillsammans/receipt-types";
import {HorseReceiptUtils} from "@atg-tillsammans/receipt-utils";
import {TeamActions} from "@atg-tillsammans/team-data-access/redux/team";
import {TeamService} from "@atg-tillsammans/team-data-access/services";
import {TeamMemberActions} from "@atg-tillsammans/team-member-data-access";
import {RoundState} from "@atg-tillsammans/types/generated";
import {PublicAppLifecycleActions} from "@atg-tillsammans-shared/app-lifecycle-actions";
import {
    PersonalizationActions,
    PersonalizationSelectors,
} from "@atg-tillsammans-shared/personalization-data-access";
import history from "atg-history";

import {TillsammansPushActionType, PushActions} from "./tillsammansPushActions";
import {
    removeByUpdateTeamMembersQuery,
    updateTeamMembersQuery,
    writeRoundBalanceQuery,
    writeRoundFragment,
    writeRoundParticipationsQuery,
    writeRoundStateFragment,
    writeSalesFragment,
} from "./tillsammansPushUtil";
import * as TillsammansPushUtils from "./tillsammansPushUtil";

import type {TillsammansPushAction} from "./tillsammansPushActions";
import type {WebsocketIds} from "@atg-frame-shared/push-saga/helpers";
import type {TeamID, HorseCoupon} from "@atg-tillsammans/types";
import type {
    BetMeta,
    BetMetaIds,
    BetWithDetails,
    Round,
} from "@atg-tillsammans/types/generated";
import type {Dispatch} from "redux";
import type {SagaIterator} from "redux-saga";

export function handleHorseCouponPushEvent(
    couponId: string,
    teamId: TeamID,
    gameId: string,
    coupon?: HorseCoupon,
    websocketIds?: WebsocketIds,
) {
    if (isEqual(websocketIds, getWebsocketIds())) return;

    if (coupon) {
        setHorseCouponPushData(
            couponId,
            HorseCouponPushEvent.UPDATE,
            teamId,
            gameId,
            coupon,
        );
    } else {
        setHorseCouponPushData(couponId, HorseCouponPushEvent.REMOVE, teamId, gameId);
    }
}

export function handleBetMetaPush(
    betMetaIds?: BetMetaIds,
    betMeta?: BetMeta,
    websocketIds?: WebsocketIds,
) {
    if (isEqual(websocketIds, getWebsocketIds())) return;

    if (betMetaIds && betMeta && betMetaIds.tsn) {
        HorseReceiptUtils.setHorseReceiptPushData({
            event: HorseReceiptPushEvent.UPDATE,
            receiptId: betMetaIds.tsn,
            betMeta,
            betMetaIds,
        });
    }
}

export function handleReceiptPush(
    couponId: string,
    receipt: BetWithDetails,
    websocketIds: WebsocketIds,
) {
    if (isEqual(websocketIds, getWebsocketIds())) return;

    HorseReceiptUtils.setHorseReceiptPushData({
        event: HorseReceiptPushEvent.ADD,
        receiptId: receipt.id,
        couponId,
        receipt,
    });
}

export function activateRound(
    round: Round,
    roundBalance: number,
    subscriptions?: Record<number, number>,
) {
    // Update round
    writeRoundFragment(round);

    // Update participation
    if (!subscriptions) return;

    writeRoundParticipationsQuery(round, subscriptions);
    writeRoundBalanceQuery(`${round.teamId}`, round.gameId, roundBalance);

    // Update my teams
    TeamService.incrementActivatedRoundsAmount(round.teamId);

    // Tracking for purchase share event on members who subscribed to the round
    // Note that the game initiator is not included in the subscriptions since tracking
    // for game initiator is done in the RoundForm.tsx
    const {gameInitiator} = round;
    const memberId = gameInitiator?.memberId;

    const updatedSubscriptions = memberId ? omit(subscriptions, memberId) : null;

    if (updatedSubscriptions) {
        Object.values(updatedSubscriptions).forEach((subscription) => {
            const gameType = GameUtils.isSportGame(round.gameId) ? "sport" : "trav";
            const price = (round.sales?.price || 0) as number;
            TillsammansAnalytics.trackPurchaseShareEvent({
                category: gameType,
                name: GameUtils.getGameType(round.gameId),
                variant: "kupong",
                cost: (subscription as number) * price,
                id: round.id,
            });
        });
    }
}

export const getMemberCallback =
    (dispatch: Dispatch<any>, memberId: number) =>
    (action: TillsammansPushAction): void => {
        switch (action.type) {
            case TillsammansPushActionType.COMMAND_RELOAD_MY_TEAMS: {
                dispatch(TeamMemberActions.fetchMemberTeams({refreshTeams: true}));
                dispatch(TeamActions.clearApplications());
                break;
            }
            case TillsammansPushActionType.EVENT_MEMBER_ONLINE:
                if (action.member) {
                    dispatch(PushActions.teamMemberLoggedIn(action.member));
                }
                break;
            case TillsammansPushActionType.EVENT_MEMBER_ENTERED_TILLSAMMANS:
                if (action.memberId && action.personalizationSettings) {
                    dispatch(
                        PersonalizationActions.receivedPersonalizationSettingsPush(
                            action.personalizationSettings,
                        ),
                    );
                }
                break;
            case TillsammansPushActionType.EVENT_TEAM_BET_PLACED:
                try {
                    log.info(
                        t({
                            id: "push.action.type.EVENT_TEAM_BET_PLACED",
                            message: "Received receipt push",
                        }),
                        {
                            tsn: action.receipt?.bet.tsn,
                        },
                    );
                } catch (e: unknown) {
                    log.error(
                        t`Error when handling event ${TillsammansPushActionType.EVENT_TEAM_BET_PLACED}`,
                        {
                            error: serializeError(e),
                        },
                    );
                }
                if (action.couponId && action.receipt) {
                    if (action.websocketIds) {
                        handleReceiptPush(
                            action.couponId,
                            action.receipt,
                            action.websocketIds,
                        );
                    }

                    dispatch(BetActions.betSuccess(action.couponId, action.receipt));

                    if (!isNil(action.roundBalance) && action.teamId && action.gameId) {
                        writeRoundBalanceQuery(
                            `${action.teamId}`,
                            action.gameId,
                            action.roundBalance,
                        );
                    }

                    writeRoundStateFragment(
                        `${action.teamId}_${action.gameId}`,
                        RoundState.PLACED,
                    );
                    break;
                }

                // Only for sport, bet is only present if sport
                if (action.bet) {
                    dispatch({
                        type: SportBetActions.SPORT_PLACE_BET_RECEIVE,
                        payload: {response: {data: action.bet}},
                    });
                }

                break;
            case TillsammansPushActionType.EVENT_TEAM_BET_FAILED:
                if (action.couponId && action.errorCode) {
                    dispatch(BetActions.betError(action.couponId, action.errorCode));
                }
                break;
            case TillsammansPushActionType.EVENT_TEAM_BET_META_UPDATED:
                handleBetMetaPush(action.betMetaIds, action.betMeta);
                break;
            case TillsammansPushActionType.EVENT_TEAM_COUPON:
                if (action.couponId && action.teamId && action.gameId) {
                    handleHorseCouponPushEvent(
                        action.couponId,
                        action.teamId,
                        action.gameId,
                        action.coupon as HorseCoupon,
                        action.websocketIds,
                    );
                }
                break;
            case TillsammansPushActionType.EVENT_TEAM_ROUND_BALANCE:
                if (!isNil(action.roundBalance) && action.teamId && action.gameId) {
                    const {round, participation, roundBalance, gameId, teamId} = action;
                    writeRoundBalanceQuery(`${teamId}`, gameId, roundBalance);
                    const wsid = getWebsocketIds();
                    if (round && round.sales && !isEqual(action.websocketIds, wsid)) {
                        writeSalesFragment(round.sales);
                    }

                    if (participation) {
                        TillsammansPushUtils.updateParticipation(participation);
                    }
                }
                break;
            case TillsammansPushActionType.EVENT_TEAM_ROUND_ACTIVATED:
                if (action.round && !isNil(action.roundBalance)) {
                    // Note: For the new activate round flow.
                    activateRound(
                        action.round,
                        action.roundBalance,
                        action.subscriptions,
                    );

                    dispatch(TeamMemberActions.invalidateMemberTeams());

                    // Note: Keeping this for legacy support
                    dispatch(
                        PushActions.roundActivated(
                            action.round,
                            action.subscriptions,
                            action.scheduledReleases,
                        ),
                    );
                }
                break;
            case TillsammansPushActionType.EVENT_TEAM_ROUND_UPDATED:
                if (action.round) {
                    writeRoundFragment(action.round);
                }
                break;
            case TillsammansPushActionType.EVENT_TEAM_MEMBER_JOINED:
                if (action.member && action.teamId) {
                    updateTeamMembersQuery(action.member, action.teamId);
                }
                break;
            case TillsammansPushActionType.EVENT_TEAM_MEMBER_LEFT:
                if (action.memberId && action.teamId) {
                    removeByUpdateTeamMembersQuery(action.memberId, action.teamId);
                }
                break;
            case TillsammansPushActionType.EVENT_TEAM_UPDATED:
            case TillsammansPushActionType.EVENT_TEAM_DELETED:
            default:
                break;
        }
    };

export function* logoutIfLoggedIn(): SagaIterator {
    const isLoggedIn = yield select(AuthSelectors.isLoggedIn);
    if (isLoggedIn) {
        yield call(history.push, "/utloggad");
    }
}

export function* subscribeOnBehalfOf(): SagaIterator {
    const isLoggedIn = yield select(AuthSelectors.isNormalLogin);

    if (!isLoggedIn) return;

    try {
        const client = yield call(getSolaceClient);
        const clientName = yield call(getClientName, client);
        const routeName = yield call(getRouteName, client);

        if (clientName && routeName) {
            yield call(fetchAuthorized, TEAM_SUBSCRIBE_SERVICE_URL, {
                body: JSON.stringify({
                    clientName,
                    route: routeName,
                }),
                method: "POST",
            });
        }
    } catch (e: unknown) {
        log.error(t`Error setting up subscription on behalf of`, {
            error: serializeError(e),
        });
    }
}

export function* subscribeUserToPush(dispatch: Dispatch<any>): SagaIterator {
    let memberId: number = yield select(PersonalizationSelectors.getMemberId);

    if (!memberId) {
        const action = yield take([
            PersonalizationActions.RECEIVED_PERSONALIZATION_SETTINGS,
            PersonalizationActions.RECEIVED_PERSONALIZATION_SETTINGS_PUSH,
        ]);
        memberId = action?.payload?.memberId;
        if (!memberId) {
            log.error("Push: No memberId found for push subscription");
            return;
        }
    }

    const memberCallback = yield call(getMemberCallback, dispatch, memberId);
    const userUnsubscribe = yield call(subscribe, "user/>", memberCallback, false, true);
    const teamUnsubscribe = yield call(subscribe, "team/>", memberCallback, false, true);
    const reconnectAction = yield take([
        UserActions.LOGOUT_INITIATED,
        RECONNECTED,
        PublicAppLifecycleActions.GOING_TO_BACKGROUND,
    ]);

    yield call(userUnsubscribe);
    yield call(teamUnsubscribe);

    if (reconnectAction.type === RECONNECTED) {
        yield call(subscribeOnBehalfOf);
        yield call(subscribeUserToPush, dispatch);
    }
}
